import * as React from 'react';
import type {ReactNode} from 'react';
import {Check} from 'lucide-react';
import {cn} from '../../../lib/cn';
import type {OptionProps} from './types';
import type {SelectValue} from '../types';

function DefaultOption<T, V extends SelectValue, L extends ReactNode>(
  props: OptionProps<T, V, L>,
) {
  const {
    option,
    index,
    isSelected,
    isHighlighted,
    isDisabled,
    isMulti,
    id,
    onClick,
    onHover,
    innerRef,
    inputValue = '',
    selectedOptions = [],
    formatOptionLabel,
  } = props;

  const labelContent = formatOptionLabel
    ? formatOptionLabel(option, {
        context: 'menu',
        inputValue,
        selectValue: selectedOptions,
      })
    : (option.label as ReactNode);

  return (
    <button
      id={id}
      ref={innerRef}
      type="button"
      role="option"
      aria-selected={isSelected}
      aria-disabled={isDisabled}
      tabIndex={-1}
      disabled={isDisabled}
      onClick={() => onClick(option)}
      onMouseMove={() => onHover(index)}
      className={cn(
        'group relative flex w-full items-start justify-between gap-2 rounded-md border-0 bg-transparent px-3 py-2.5 text-left text-[15px] font-medium leading-5 text-[var(--chekin-color-brand-navy)] outline-none transition-colors duration-100',
        !isDisabled && 'cursor-pointer hover:bg-[var(--chekin-color-surface-pressed)]',
        isHighlighted && !isDisabled && 'bg-[var(--chekin-color-surface-pressed)]',
        isSelected &&
          'bg-[var(--chekin-color-surface-autocomplete)] font-semibold text-[var(--chekin-color-brand-blue)]',
        isDisabled && 'cursor-not-allowed text-[var(--chekin-color-gray-2)]',
      )}
    >
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="break-words inline-flex">{labelContent}</span>
        {option.description && (
          <span className="break-words text-[12px] font-medium italic leading-4 text-[var(--chekin-color-gray-1)]">
            {option.description}
          </span>
        )}
      </span>
      {isSelected && (
        <Check
          aria-hidden="true"
          className={cn(
            'shrink-0 text-[var(--chekin-color-brand-blue)]',
            isMulti ? 'ml-1 mt-0.5 h-3.5 w-3.5' : 'ml-2 mt-0.5 h-4 w-4',
          )}
          strokeWidth={2.5}
        />
      )}
    </button>
  );
}

export {DefaultOption};
